import type { MetaFunction } from '@remix-run/node'
import { titleSuffix } from './title'

type Meta = ReturnType<MetaFunction>

const title =
  import.meta.env.VITE_MODE === 'ppg'
    ? `PayPayグルメ（ペイペイグルメ） ${titleSuffix}`
    : `レストラン予約 ${titleSuffix}`

const description =
  import.meta.env.VITE_MODE === 'ppg'
    ? 'PayPayグルメ（ぺいぺいグルメ）は PayPayポイントがお得にもらえるグルメ予約サイトです。全国のレストランからお店探しができ、ネット予約でPayPayポイントがもらえます。'
    : 'ワンランク上のレストラン予約。地域やジャンルからカンタン検索、ネット予約。一休だけの限定メニューやお店・メニューの写真・利用者の感想などレストラン情報満載。記念日ディナー、接待に是非！'

export const siteName = import.meta.env.VITE_MODE === 'ppg' ? 'PayPayグルメ' : '一休.comレストラン'
const url =
  import.meta.env.VITE_MODE === 'ppg'
    ? 'https://paypaygourmet.yahoo.co.jp'
    : 'https://restaurant.ikyu.com'

const ogpImage =
  import.meta.env.VITE_MODE === 'ppg'
    ? 'https://restaurant.img-ikyu.com/paypaygourmet/img/ogp.png'
    : 'https://restaurant.img-ikyu.com/rsImg/ogp/ogp.jpg'

export function baseMeta(): Meta {
  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'article' },
    { property: 'og:site_name', content: siteName },
    { property: 'og:url', content: url },
    { property: 'og:image', content: ogpImage },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@ikyu_restaurant' },
    { name: 'twitter:creator', content: '@ikyu_restaurant' },
    { name: 'twitter:image', content: ogpImage },
  ]
}

export function override(meta: Meta): Meta {
  return mergeMeta(baseMeta(), meta)
}

export function mergeMeta(base: Meta, override: Meta): Meta {
  return [
    ...mergeTitle(base, override),
    ...mergeLinks(base, override),
    ...mergeProps(base, override),
    ...mergeNames(base, override),
    ...mergeLinkedData(base, override),
  ]
}

export type MetaTitleEntry = { title: string }
export type MetaPropEntry = { property: string; content: string }
export type MetaNameEntry = { name: string; content: string }
export type MetaLinkEntry = { tagName: string; rel: string; href: string }

function mergeTitle(base: Meta, override: Meta): Meta {
  const overrideTitle = override.find((e) => 'title' in e)
  if (overrideTitle) {
    return [overrideTitle]
  }
  const baseTitle = base.find((e) => 'title' in e)
  if (baseTitle) {
    return [baseTitle]
  }
  return []
}
if (import.meta.vitest) {
  const { test, expect } = import.meta.vitest
  test('mergeTitle', () => {
    expect(mergeTitle([{ title: 'base' }], [])).toEqual([{ title: 'base' }])
    expect(mergeTitle([], [{ title: 'override' }])).toEqual([{ title: 'override' }])
    expect(mergeTitle([{ title: 'base' }], [{ title: 'override' }])).toEqual([
      { title: 'override' },
    ])
  })
}

function mergeLinks(base: Meta, override: Meta): Meta {
  return [...toMetaLinks(base), ...toMetaLinks(override)]
}

function toMetaLinks(base: Meta): MetaLinkEntry[] {
  return base.filter(isMetaLinkEntry)
}

function isMetaLinkEntry(entry: Object): entry is MetaLinkEntry {
  return (
    'tagName' in entry &&
    entry.tagName === 'link' &&
    'rel' in entry &&
    typeof entry.rel === 'string' &&
    'href' in entry &&
    typeof entry.href === 'string'
  )
}

function mergeProps(base: Meta, override: Meta): Meta {
  const baseMap = toMetaPropMap(base)
  const overrideMap = toMetaPropMap(override)
  const mergedMap = new Map([...baseMap, ...overrideMap])
  return Array.from(mergedMap.values())
}
if (import.meta.vitest) {
  const { test, expect } = import.meta.vitest
  test('mergeProps', () => {
    expect(mergeProps([{ property: 'og:type', content: 'website' }], [])).toEqual([
      { property: 'og:type', content: 'website' },
    ])
    expect(mergeProps([], [{ property: 'og:type', content: 'article' }])).toEqual([
      { property: 'og:type', content: 'article' },
    ])
    expect(
      mergeProps(
        [{ property: 'og:type', content: 'website' }],
        [{ property: 'og:type', content: 'article' }]
      )
    ).toEqual([{ property: 'og:type', content: 'article' }])
    expect(
      mergeProps(
        [
          { property: 'og:title', content: 'title' },
          { property: 'og:type', content: 'website' },
          { property: 'og:url', content: '/105355' },
        ],
        [
          { property: 'og:type', content: 'article' },
          { property: 'og:description', content: 'description' },
          { property: 'og:url', content: '/105355/access' },
        ]
      )
    ).toEqual([
      { property: 'og:title', content: 'title' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: '/105355/access' },
      { property: 'og:description', content: 'description' },
    ])
  })
}

function mergeNames(base: Meta, override: Meta): Meta {
  const baseMap = toMetaNameMap(base)
  const overrideMap = toMetaNameMap(override)
  const mergedMap = new Map([...baseMap, ...overrideMap])
  return Array.from(mergedMap.values())
}
if (import.meta.vitest) {
  const { test, expect } = import.meta.vitest
  test('mergeNames', () => {
    expect(mergeNames([{ name: 'twitter:site', content: 'base' }], [])).toEqual([
      { name: 'twitter:site', content: 'base' },
    ])
    expect(mergeNames([], [{ name: 'twitter:site', content: 'override' }])).toEqual([
      { name: 'twitter:site', content: 'override' },
    ])
    expect(
      mergeNames(
        [{ name: 'twitter:site', content: 'base' }],
        [{ name: 'twitter:site', content: 'override' }]
      )
    ).toEqual([{ name: 'twitter:site', content: 'override' }])
    expect(
      mergeNames(
        [
          { name: 'twitter:title', content: 'title' },
          { name: 'twitter:site', content: 'base' },
        ],
        [
          { name: 'twitter:site', content: 'override' },
          { name: 'twitter:description', content: 'description' },
        ]
      )
    ).toEqual([
      { name: 'twitter:title', content: 'title' },
      { name: 'twitter:site', content: 'override' },
      { name: 'twitter:description', content: 'description' },
    ])
  })
}

function mergeLinkedData(base: Meta, override: Meta): Meta {
  const overrideLinkedData = override.find((e) => 'script:ld+json' in e)
  if (overrideLinkedData) {
    return [overrideLinkedData]
  }
  const baseLinkedData = base.find((e) => 'script:ld+json' in e)
  if (baseLinkedData) {
    return [baseLinkedData]
  }
  return []
}
if (import.meta.vitest) {
  const { test, expect } = import.meta.vitest
  test('mergeLinkedData', () => {
    expect(mergeLinkedData([{ 'script:ld+json': { '@content': 'base' } }], [])).toEqual([
      { 'script:ld+json': { '@content': 'base' } },
    ])
    expect(mergeLinkedData([], [{ 'script:ld+json': { '@content': 'override' } }])).toEqual([
      { 'script:ld+json': { '@content': 'override' } },
    ])
    expect(
      mergeLinkedData(
        [{ 'script:ld+json': { '@content': 'base' } }],
        [{ 'script:ld+json': { '@content': 'override' } }]
      )
    ).toEqual([{ 'script:ld+json': { '@content': 'override' } }])
  })
}

function toMetaPropMap(meta: Meta): Map<string, MetaPropEntry> {
  return meta.filter(isMetaPropEntry).reduce((acc, e) => {
    return acc.set(e.property, e)
  }, new Map())
}

function toMetaNameMap(meta: Meta): Map<string, MetaNameEntry> {
  return meta.filter(isMetaNameEntry).reduce((acc, e) => {
    return acc.set(e.name, e)
  }, new Map())
}

export function isMetaPropEntry(entry: Object): entry is MetaPropEntry {
  return (
    'property' in entry &&
    typeof entry.property === 'string' &&
    entry.property !== '' &&
    hasContent(entry)
  )
}

export function isMetaNameEntry(entry: Object): entry is MetaNameEntry {
  return 'name' in entry && typeof entry.name === 'string' && entry.name !== '' && hasContent(entry)
}

function hasContent(entry: Object): entry is { content: string } {
  return 'content' in entry && typeof entry.content === 'string' && entry.content !== ''
}

export function isMetaTitle(entry: Object): entry is MetaTitleEntry {
  return 'title' in entry && typeof entry.title === 'string' && entry.title !== ''
}
